import { useFormikContext } from 'formik'
import { ReactElement } from 'react'
import { defineMessages, FormattedMessage, useIntl } from 'react-intl'

import { Column } from 'components/v2/Column'
import Heading from 'components/v2/Heading'
import { Row } from 'components/v2/Row'
import { EBrandCoverage, IVehicleFeaturesFormValues } from 'features/Vehicles/types'
import { Select } from 'components/v2/Select'
import { AMENITIES } from 'features/Lines/components/Amenities/constants'
import { safeFormatMessage } from 'utils/intl'
import { amenitiesMessages } from 'features/Lines/components/Amenities/messages'
import { SelectTags } from 'components/v2/SelectTags'
import { Input } from 'components/Input'

const BRAND_COVERAGE_LIST = [
  EBrandCoverage.CompleteBranding,
  EBrandCoverage.MediumBranding,
  EBrandCoverage.MinimumBranding,
  EBrandCoverage.WithoutBranding
]

const brandCoverageMessages = defineMessages({
  [EBrandCoverage.CompleteBranding]: {
    id: 'brandCoverage.completeBranding',
    defaultMessage: 'Complete branding'
  },
  [EBrandCoverage.MediumBranding]: {
    id: 'brandCoverage.mediumBranding',
    defaultMessage: 'Medium branding'
  },
  [EBrandCoverage.MinimumBranding]: {
    id: 'brandCoverage.minimumBranding',
    defaultMessage: 'Minimum branding'
  },
  [EBrandCoverage.WithoutBranding]: {
    id: 'brandCoverage.withoutBranding',
    defaultMessage: 'Without branding'
  }
})

const AMENITIES_LIST = [
  AMENITIES.WIFI,
  AMENITIES.TV,
  AMENITIES.ETICKET,
  AMENITIES.WC,
  AMENITIES.AC,
  AMENITIES.CHAIR,
  AMENITIES.PLUG,
  AMENITIES.LIGHT,
  AMENITIES.EXCURSION_TRIP,
  AMENITIES.LUNCHBOX,
  AMENITIES.GUIDE
]

const getFilteredAmenities = (values: string[]): string[] => {
  return AMENITIES_LIST.filter((amenity) => {
    return values.indexOf(amenity) === -1
  })
}

const messages = defineMessages({
  brandCoveragePlaceholder: {
    id: 'vehicles.form.features.brandCoverage.placeholder',
    defaultMessage: 'Select the branding coverage'
  },
  amenitiesPlaceholder: {
    id: 'vehicles.form.features.amenities.placeholder',
    defaultMessage: 'Select amenities'
  },
  makePlaceholder: {
    id: 'vehicles.form.features.make.placeholder',
    defaultMessage: 'Enter the make'
  },
  modelPlaceholder: {
    id: 'vehicles.form.features.model.placeholder',
    defaultMessage: 'Enter the model'
  },
  yearPlaceholder: {
    id: 'vehicles.form.features.year.placeholder',
    defaultMessage: 'Select the year'
  }
})

export const FeaturesForm = (): ReactElement => {
  const intl = useIntl()
  const { values, handleChange, setFieldValue, handleBlur, touched, errors } =
    useFormikContext<IVehicleFeaturesFormValues>()

  const handleAmenitiesChange = (values: string[]): void => {
    setFieldValue('features.amenities', values)
  }

  const handleYearChange = ({ target: { value } }: React.ChangeEvent<HTMLSelectElement>): void => {
    setFieldValue('features.year', value ? parseInt(value) : null)
  }

  return (
    <>
      <Heading.H4>
        <FormattedMessage id="vehicles.form.features.title" defaultMessage="Features" />
      </Heading.H4>
      <Row noMargin>
        <Column width="50%">
          <Select
            id="features.brandCoverage"
            value={values.features.brandCoverage || ''}
            onChange={handleChange}
            onBlur={handleBlur}
            touched={touched.features?.brandCoverage}
            errors={errors.features?.brandCoverage}
            label={
              <FormattedMessage id="vehicles.form.features.brandCoverage.label" defaultMessage="Branding coverage" />
            }
            optional
          >
            <option value="">{intl.formatMessage(messages.brandCoveragePlaceholder)}</option>
            {BRAND_COVERAGE_LIST.map((brandCoverage): ReactElement => {
              return (
                <option key={brandCoverage} value={brandCoverage}>
                  {intl.formatMessage(brandCoverageMessages[brandCoverage])}
                </option>
              )
            })}
          </Select>
        </Column>
      </Row>
      <Row noMargin>
        <Column>
          <Input
            id="features.make"
            value={values.features.make || ''}
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder={intl.formatMessage(messages.makePlaceholder)}
            label={<FormattedMessage id="vehicles.form.features.make.label" defaultMessage="Make" />}
            touched={touched.features?.make}
            errors={errors.features?.make}
            optional
          />
        </Column>
        <Column>
          <Input
            id="features.model"
            value={values.features.model || ''}
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder={intl.formatMessage(messages.modelPlaceholder)}
            label={<FormattedMessage id="vehicles.form.features.model.label" defaultMessage="Model" />}
            touched={touched.features?.model}
            errors={errors.features?.model}
            optional
          />
        </Column>
        <Column>
          <Select
            id="features.year"
            value={values.features.year ? values.features.year.toString() : ''}
            onChange={handleYearChange}
            onBlur={handleBlur}
            touched={touched.features?.year}
            errors={errors.features?.year}
            label={<FormattedMessage id="vehicles.form.features.year.label" defaultMessage="Year" />}
            optional
          >
            <option value="">{intl.formatMessage(messages.yearPlaceholder)}</option>
            {Array.from({ length: 30 }, (_, i): number => {
              return new Date().getFullYear() - i
            }).map((year): ReactElement => {
              return (
                <option key={year} value={year}>
                  {year}
                </option>
              )
            })}
          </Select>
        </Column>
      </Row>
      <Row noMargin>
        <Column width="50%">
          <SelectTags
            id="features.amenities"
            values={values.features.amenities}
            items={getFilteredAmenities(values.features.amenities)}
            getTagLabel={(value: string): string => {
              return safeFormatMessage({ intl, messages: amenitiesMessages, key: value })
            }}
            onChange={handleAmenitiesChange}
            onBlur={handleBlur}
            label={<FormattedMessage id="vehicles.form.features.amenities.label" defaultMessage="Amenities" />}
            placeholder={intl.formatMessage(messages.amenitiesPlaceholder)}
            optional
          />
        </Column>
      </Row>
    </>
  )
}
